/* eslint-disable max-len */
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useEffect, useRef, useState } from 'react';
import { withHousehold } from 'Context/HouseholdContext';
import useUserContext from 'Context/UserContext';
import Document from 'Components/Universal/Print/Document';
import usePortraitOrientation from 'Hooks/usePortraitOrientation';

import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import Main from 'Components/Blueprint/UI/Main';
import Section from 'Components/Blueprint/UI/Section';

import Header from 'Components/Blueprint/UI/Header';
import FamilyInfo from 'Components/Blueprint/Sections/FamilyInfo';
import FamilyInfoHeader from 'Components/Blueprint/Sections/FamilyInfoHeader';
import FinancialSummary from 'Components/Blueprint/Sections/FinancialSummary';
import ClientProfile from 'Components/Blueprint/Sections/ClientProfile';
import EstatePlanning from 'Components/Blueprint/Sections/EstatePlanning';
import RiskManagement from 'Components/Blueprint/Sections/RiskManagement';
import Footnotes from 'Components/Blueprint/Sections/Footnotes';
import StrategiesRecos from 'Components/Blueprint/Sections/StrategiesRecos';
import './index.css';
import useMobileLayout from 'Hooks/useMobileLayout';
import useBackgroundColor from 'Hooks/useBackgroundColor';
import { useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  FDBox, FDContentContainer, FDEmptyState, FDStack,
} from '@facet-wealth/design';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import Subnav from './UI/Subnav';

const NON_RETIREMENT_ACCOUNT = 'non_retirement_account';
const EMPLOYER_SPONSORED_PLAN = 'employer_sponsored_plan';
const PERSONAL_RETIREMENT_ACCOUNT = 'personal_retirement_account';

function filterQuestionnaireNotesArray(qsn = [], type) {
  const ret = [];
  qsn.forEach((el) => {
    if (el.attributes.type === type) {
      ret.push(el);
    }
  });

  return ret;
}

function Blueprint() {
  useScrollTopOnMount();
  const [subnavRefs] = useState({
    overview: useRef(null),
    financialSummary: useRef(null),
    strategiesRecos: useRef(null),
    clientProfile: useRef(null),
    estatePlanning: useRef(null),
    riskManagement: useRef(null),
    footnotes: useRef(null),
  });
  const { rootNodeProps } = useEventTrackingLogEvent({
    nodeName: 'Blueprint',
    isPageView: true,
  });
  const { isAgent } = useUserContext();
  const isPortrait = usePortraitOrientation();
  const isMobile = useMobileLayout();
  const theme = useTheme();
  useBackgroundColor(theme.palette.facet.lightGray);

  const { householdResource } = useSimpleHousehold();

  const {
    getBlueprintAssets,
    getGroupLifeInsuranceAccounts,
    getLifeInsuranceAccounts,
    getDefinedPensionPlans,
    getSocialSecurityBenefits,
    getWills,
    getGeneralPowerOfAttorney,
    getAdvancedHealthcareDirective,
    getTrusts,
    getOtherAdvisors,
    getPCInsurancePolicies,
    getMedicareInsurancePolicies,
    getPrivateHealthInsurancePolicies,
    getHealthSavingsAccounts,
    getFlexibleSpendingAccounts,
    getShortTermDisabilityInsurancePolicies,
    getLongTermDisabilityInsurancePolicies,
    getPrivateDisabilityInsurancePolicies,
    getLongTermCareInsurancePolicies,
    getQuestionnaireSectionNotes,
    getClientsByHousehold,
    fetchActionItemsByHouseholdID,
    getIncomeInfosByHouseholdId,
    getExpenseSummaryByHouseholdId,
    getGoals,
    getInvestmentProfileAndKnowledgeByHouseholdID,
  } = useSwaggerlessContext();

  const [PCInsurancePolicies, setPCInsurancePolicies] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [advancedHealthcareDirectives, setAdvancedHealthcareDirectives] = useState([]);
  const [blueprintAssets, setBlueprintAssets] = useState([]);
  const [cashFlow, setCashFlow] = useState([]);
  const [clients, setClients] = useState([]);
  const [definedPensionPlans, setDefinedPensionPlans] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [flexibleSpendingAccounts, setFlexibleSpendingAccounts] = useState([]);
  const [generalPowerOfAttorney, setGeneralPowerOfAttorney] = useState([]);
  const [groupLifeInsuranceAccounts, setGroupLifeInsuranceAccounts] = useState([]);
  const [healthSavingsAccounts, setHealthSavingsAccounts] = useState([]);
  const [income, setIncome] = useState([]);
  const [investmentProfileAndKnowledge, setInvestmentProfileAndKnowledge] = useState(null);
  const [lifeInsuranceAccounts, setLifeInsuranceAccounts] = useState([]);
  const [longTermCareInsurancePolicies, setLongTermCareInsurancePolicies] = useState([]);
  const [longTermDisabilityInsurancePolicies, setLongTermDisabilityInsurancePolicies] = useState([]);
  const [medicareInsurancePolicies, setMedicareInsurancePolicies] = useState([]);
  const [otherAdvisors, setOtherAdvisors] = useState([]);
  const [privateDisabilityInsurancePolicies, setPrivateDisabilityInsurancePolicies] = useState([]);
  const [privateHealthInsurancePolicies, setPrivateHealthInsurancePolicies] = useState([]);
  const [questionnaireSectionNotes, setQuestionnaireSectionNotes] = useState([]);
  const [shortTermDisabilityInsurancePolicies, setShortTermDisabilityInsurancePolicies] = useState([]);
  const [socialSecurityBenefits, setSocialSecurityBenefits] = useState([]);
  const [trusts, setTrusts] = useState([]);
  const [wills, setWills] = useState([]);

  const swaggerlessCalls = [
    (householdId) => fetchActionItemsByHouseholdID(householdId).then((res) => setActionItems(res.data)),
    (householdId) => getAdvancedHealthcareDirective(householdId).then((res) => setAdvancedHealthcareDirectives(res.data)),
    (householdId) => getBlueprintAssets(householdId).then((res) => setBlueprintAssets(res.data)),
    (householdId) => getClientsByHousehold(householdId).then((res) => setClients(res.data)),
    (householdId) => getDefinedPensionPlans(householdId).then((res) => setDefinedPensionPlans(res.data)),
    (householdId) => getExpenseSummaryByHouseholdId(householdId).then((res) => setCashFlow(res.data)),
    (householdId) => getFlexibleSpendingAccounts(householdId).then((res) => setFlexibleSpendingAccounts(res.data)),
    (householdId) => getGeneralPowerOfAttorney(householdId).then((res) => setGeneralPowerOfAttorney(res.data)),
    (householdId) => getGoals(householdId).then((res) => setFinancialGoals(res.data)),
    (householdId) => getGroupLifeInsuranceAccounts(householdId).then((res) => setGroupLifeInsuranceAccounts(res.data)),
    (householdId) => getHealthSavingsAccounts(householdId).then((res) => setHealthSavingsAccounts(res.data)),
    (householdId) => getIncomeInfosByHouseholdId(householdId).then((res) => setIncome(res.data)),
    (householdId) => getInvestmentProfileAndKnowledgeByHouseholdID(householdId).then((res) => setInvestmentProfileAndKnowledge(res.data)),
    (householdId) => getLifeInsuranceAccounts(householdId).then((res) => setLifeInsuranceAccounts(res.data)),
    (householdId) => getLongTermCareInsurancePolicies(householdId).then((res) => setLongTermCareInsurancePolicies(res.data)),
    (householdId) => getLongTermDisabilityInsurancePolicies(householdId).then((res) => setLongTermDisabilityInsurancePolicies(res.data)),
    (householdId) => getMedicareInsurancePolicies(householdId).then((res) => setMedicareInsurancePolicies(res.data)),
    (householdId) => getOtherAdvisors(householdId).then((res) => setOtherAdvisors(res.data)),
    (householdId) => getPCInsurancePolicies(householdId).then((res) => setPCInsurancePolicies(res.data)),
    (householdId) => getPrivateDisabilityInsurancePolicies(householdId).then((res) => setPrivateDisabilityInsurancePolicies(res.data)),
    (householdId) => getPrivateHealthInsurancePolicies(householdId).then((res) => setPrivateHealthInsurancePolicies(res.data)),
    (householdId) => getQuestionnaireSectionNotes(householdId).then((res) => setQuestionnaireSectionNotes(res.data)),
    (householdId) => getShortTermDisabilityInsurancePolicies(householdId).then((res) => setShortTermDisabilityInsurancePolicies(res.data)),
    (householdId) => getSocialSecurityBenefits(householdId).then((res) => setSocialSecurityBenefits(res.data)),
    (householdId) => getTrusts(householdId).then((res) => setTrusts(res.data)),
    (householdId) => getWills(householdId).then((res) => setWills(res.data)),
  ];

  const [outstandingRequests, setOutstandingRequests] = useState(swaggerlessCalls.length);

  useEffect(() => {
    if (!householdResource) {
      return;
    }
    for (const swaggerlessCall of swaggerlessCalls) {
      swaggerlessCall(householdResource.id())
        .finally(() => setOutstandingRequests((prev) => prev - 1));
    }
  }, [householdResource]);

  // Wait for all swaggerless calls to finish so it emulates what was happening before
  if (outstandingRequests > 0) {
    return null;
  }

  function setRef(component, ref) {
    subnavRefs[component] = ref;
  }

  if (isMobile && isPortrait) {
    return (
      <Box height={`calc(100vh - ${theme.heights.mobileBottomNavigationHeight}px)`} display="flex">
        <FDStack direction="column" justifyContent="center" flexGrow={1}>
          <FDEmptyState title="Please rotate your device to view" icon="screenRotation" data-test="blueprint-page" />
        </FDStack>
      </Box>
    );
  }

  return (
    <FDContentContainer
      maxWidth="xl"
      {...rootNodeProps}
      data-test="blueprint-page"
    >
      <FDStack spacing={2}>
        {!isMobile && isAgent ? (
          <Subnav
            subnavRefs={subnavRefs}
          />) : null}
        <FDBox width="100%">
          <Header clients={clients} />
          {/* Don't remove this div or its styling. The padding is being hoisted up from div.blueprint-main so that it doesn't get rendered in the file preview. */}
          <Box className="blueprint" paddingBottom={2}>
            <Document>
              <Main>
                {/*
            |--------------------------------------------------------------------------
            | Family Info
            |--------------------------------------------------------------------------
            */}
                <div id="blueprint-family-info">
                  <Section
                    heading="Family Info"
                    headingClassName="inline"
                    className="family-info"
                    content={<FamilyInfoHeader />}
                    setRef={ref => setRef('overview', ref)}
                  >
                    <FamilyInfo
                      financialGoals={financialGoals}
                      isAgent={isAgent}
                    />
                  </Section>
                </div>
                {/*
            |--------------------------------------------------------------------------
            | Financial Summary
            |--------------------------------------------------------------------------
            */}
                {investmentProfileAndKnowledge && (
                  <div id="blueprint-financial-summary">
                    <Section
                      heading="Financial Summary"
                      className="financial-summary"
                      setRef={ref => setRef('financialSummary', ref)}
                    >
                      <FinancialSummary
                        income={income}
                        cashFlow={cashFlow}
                        investmentProfileAndKnowledge={investmentProfileAndKnowledge}
                        isAgent={isAgent}
                      />
                    </Section>
                  </div>
                )}
                {/*
            |--------------------------------------------------------------------------
            | Strategies & Recommendations
            |--------------------------------------------------------------------------
            */}
                <div id="blueprint-strategies">
                  <Section
                    heading="Strategies &amp; Recommendations"
                    className="strategies-recos"
                    setRef={ref => setRef('strategiesRecos', ref)}
                  >
                    <StrategiesRecos
                      strategies={filterQuestionnaireNotesArray(
                        questionnaireSectionNotes,
                        'strategy',
                      )}
                      recos={filterQuestionnaireNotesArray(
                        questionnaireSectionNotes,
                        'recommendation',
                      )}
                      actionItems={actionItems}
                      isAgent={isAgent}
                    />
                  </Section>
                </div>
                {/*
            |--------------------------------------------------------------------------
            | Client Profile
            |--------------------------------------------------------------------------
            */}
                <div id="blueprint-client-profile">
                  <Section
                    heading="Client Profile"
                    className="client-profile"
                    setRef={ref => setRef('clientProfile', ref)}
                  >
                    <ClientProfile
                      lifeInsuranceAccounts={lifeInsuranceAccounts}
                      definedPensionPlans={definedPensionPlans}
                      socialSecurityBenefits={socialSecurityBenefits}
                      isAgent={isAgent}
                    />
                  </Section>
                </div>
                {/*
            |--------------------------------------------------------------------------
            | Estate Planning
            |--------------------------------------------------------------------------
            */}
                <div id="blueprint-estate-planning">
                  <Section
                    heading="Estate Planning"
                    className="estate-planning"
                    setRef={ref => setRef('estatePlanning', ref)}
                  >
                    <EstatePlanning
                      wills={wills}
                      generalPowerOfAttorney={generalPowerOfAttorney}
                      advancedHealthcareDirectives={advancedHealthcareDirectives}
                      trusts={trusts}
                      nonRetirementAccounts={blueprintAssets.filter(
                        account => account.attributes.accountType === NON_RETIREMENT_ACCOUNT,
                      )}
                      personalRetirementAccounts={blueprintAssets.filter(
                        account => account.attributes.accountType
                      === PERSONAL_RETIREMENT_ACCOUNT,
                      )}
                      employerSponsoredPlans={blueprintAssets.filter(
                        account => account.attributes.accountType === EMPLOYER_SPONSORED_PLAN,
                      )}
                      definedPensionPlans={definedPensionPlans}
                      groupLifeInsuranceAccounts={groupLifeInsuranceAccounts}
                      lifeInsuranceAccounts={lifeInsuranceAccounts}
                      otherAdvisors={otherAdvisors}
                      clients={clients}
                      isAgent={isAgent}
                    />
                  </Section>
                </div>
                {/*
            |--------------------------------------------------------------------------
            | Risk Management & Insurance
            |--------------------------------------------------------------------------
            */}
                <div id="blueprint-risk-management-insurance">
                  <Section
                    heading="Risk Management &amp; Insurance"
                    className="risk-management-insurance"
                    setRef={ref => setRef('riskManagement', ref)}
                  >
                    <RiskManagement
                      PCInsurancePolicies={PCInsurancePolicies}
                      medicareInsurancePolicies={medicareInsurancePolicies}
                      healthSavingsAccounts={healthSavingsAccounts}
                      flexibleSpendingAccounts={flexibleSpendingAccounts}
                      privateHealthInsurancePolicies={privateHealthInsurancePolicies}
                      shortTermDisabilityInsurancePolicies={
                    shortTermDisabilityInsurancePolicies
                  }
                      longTermDisabilityInsurancePolicies={
                    longTermDisabilityInsurancePolicies
                  }
                      privateDisabilityInsurancePolicies={
                    privateDisabilityInsurancePolicies
                  }
                      longTermCareInsurancePolicies={longTermCareInsurancePolicies}
                      lifeInsuranceAccounts={lifeInsuranceAccounts}
                      groupLifeInsuranceAccounts={groupLifeInsuranceAccounts}
                      isAgent={isAgent}
                    />
                  </Section>
                </div>
                {/*
            |--------------------------------------------------------------------------
            | Notes
            |--------------------------------------------------------------------------
            */}
                <div id="blueprint-footnotes">
                  <Section
                    heading="Footnotes"
                    className="notes"
                    setRef={ref => setRef('footnotes', ref)}
                  >
                    <Footnotes
                      footnotes={filterQuestionnaireNotesArray(
                        questionnaireSectionNotes,
                        'footnote',
                      )}
                      isAgent={isAgent}
                    />
                  </Section>
                </div>
              </Main>
            </Document>
          </Box>
        </FDBox>
      </FDStack>
    </FDContentContainer>
  );
}

Blueprint.propTypes = {
};

Blueprint.defaultProps = {
};

const ApiConnectedBlueprint = Blueprint;

export default withHousehold(ApiConnectedBlueprint);
